import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import PageContainer from "../../components/PageContainer";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import { getEmployee } from "../../services/getEmployee";
import { updateEmployee } from "../../services/updateEmployee";
import { getResetPassword } from "../../services/resetPasswords";
import RolesListBox from "./RolesListBox";
import toast, { Toaster } from "react-hot-toast";
import { getLoggedInEmployee } from "../../services/getLoggedInEmployee";

const ROLES = ["User", "Admin"];

function EditEmployeeForm() {
    const [loggedInEmployee, setLoggedInEmployee] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        role: 0,
    });
    const [loading, setLoading] = useState(true);
    const params = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            const employee = await getEmployee(params.id);
            if (!employee.success) return navigate("/employees");

            setForm({
                firstName: employee.data.employeeResponse.firstName,
                lastName: employee.data.employeeResponse.lastName,
                email: employee.data.employeeResponse.email,
                role: employee.data.employeeResponse.role,
            });
            setLoading(false);
        };

        const getCurrentEmployee = async () => {
            const employee = await getLoggedInEmployee();
            if (employee.success) {
                setLoggedInEmployee(employee.data.employeeResponse);
            }
        };

        fetchData();
        getCurrentEmployee();
    }, [params.id, navigate]);

    const hasPermission = () => loggedInEmployee?.role === 1;

    const saveDetails = async () => {
        if (!hasPermission()) {
            toast.error("You don't have permission to do this.");
            return;
        }

        if (!form.firstName || !form.lastName || !form.email) {
            toast.error("Please fill in all fields.");
            return;
        }

        try {
            const response = await updateEmployee(params.id, form);
            if (response.data.success) {
                toast.success(response.data.message);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error("An error occurred while saving.");
            console.error(error);
        }
    };

    const resetPassword = async () => {
        if (!hasPermission()) {
            toast.error("You don't have permission to do this.");
            return;
        }

        try {
            const res = await getResetPassword(form.email);
            if (res.data?.success) {
                toast.success(res.data.message);
            } else {
                toast.error(res.data?.message || "Failed to send reset email.");
            }
        } catch (error) {
            toast.error("An error occurred while sending reset email.");
            console.error(error);
        }
    };

    const onHandleChangeRole = (role) => {
        if (form.role !== role) {
            setForm((prev) => ({ ...prev, role }));
        }
    };

    return (
        <section>
            <Toaster />
            <div className="flex h-screen overflow-hidden">
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <PageContainer
                        isLoading={loading}
                        loadingText="Loading employee details..."
                    >
                        <main>
                            <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                                <div className="flex flex-col items-start max-w-2xl">
                                    <Breadcrumbs
                                        home="/employees"
                                        pages={[
                                            {
                                                name: "Edit",
                                                href: `/employees/${params.id}`,
                                                current: true,
                                            },
                                        ]}
                                    />

                                    <div className="w-full mt-12 mb-8 sm:flex sm:justify-between sm:items-center">
                                        <div className="mb-4 sm:mb-0">
                                            <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                                Edit Employee
                                            </h1>
                                        </div>
                                    </div>

                                    <div className="relative w-full bg-white border rounded-sm shadow-lg border-slate-200">
                                        <header className="px-5 py-4 border-b border-slate-200">
                                            <h2 className="font-semibold text-slate-800">
                                                Details
                                            </h2>
                                        </header>

                                        <div className="px-5 py-4 space-y-5">
                                            {[
                                                "First Name",
                                                "Last Name",
                                                "Email",
                                            ].map((field, index) => {
                                                const fieldKey = field
                                                    .toLowerCase()
                                                    .replace(/ (\w)/g, (_, c) =>
                                                        c.toUpperCase()
                                                    );
                                                return (
                                                    <div key={index}>
                                                        <label
                                                            htmlFor={fieldKey}
                                                            className="block text-sm font-medium text-gray-700"
                                                        >
                                                            {field}
                                                        </label>
                                                        <div className="mt-1">
                                                            <input
                                                                type="text"
                                                                id={fieldKey}
                                                                value={
                                                                    form[fieldKey]
                                                                }
                                                                onChange={(e) =>
                                                                    setForm({
                                                                        ...form,
                                                                        [fieldKey]:
                                                                            e.target
                                                                                .value,
                                                                    })
                                                                }
                                                                className="w-full max-w-sm p-1 px-2 border rounded-sm shadow-sm border-slate-200 focus:ring-0 focus:outline-none"
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {hasPermission() && (
                                                <div className="mt-1">
                                                    <RolesListBox
                                                        roles={ROLES}
                                                        role={form.role}
                                                        onChange={onHandleChangeRole}
                                                    />
                                                </div>
                                            )}

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">
                                                    Send Reset Password Email
                                                </label>
                                                <button
                                                    onClick={resetPassword}
                                                    type="button"
                                                    className="z-50 inline-flex justify-center px-4 py-2 mt-4 ml-3 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0"
                                                >
                                                    Reset Password
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end w-full">
                                        <button
                                            onClick={saveDetails}
                                            type="submit"
                                            className="z-50 inline-flex justify-center px-4 py-2 mt-4 ml-3 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </PageContainer>
                </div>
            </div>
        </section>
    );
}

export default EditEmployeeForm;
